import type { Ref } from 'vue';
import { ref, computed } from 'vue';
import { isValidDate } from '@/shared/lib';
import type { FunctionalActivityItem } from '../ui/UserFormContent.vue';
import { useUserFormValidation } from './useUserFormValidation';

interface EditUserSnapshot {
  name: string;
  registrationNumber: string;
  username: string;
  email: string;
  admissionDate: number | null;
  dismissalDate: number | null;
  status: string;
  isSuperadmin: boolean;
  functionalActivities: string[];
}

interface UseEditUserFormValidationParams {
  name: Ref<string>;
  username: Ref<string>;
  email: Ref<string>;
  registrationNumber: Ref<string>;
  admissionDate: Ref<Date | null>;
  dismissalDate: Ref<Date | null>;
  status: Ref<string>;
  isSuperadmin: Ref<boolean>;
  functionalActivities: Ref<FunctionalActivityItem[]>;
  isUpdating: Ref<boolean>;
  isLoadingUser: Ref<boolean>;
}

export function useEditUserFormValidation(params: UseEditUserFormValidationParams) {
  const isHydratingUserData = ref(false);
  const {
    serverEmailError,
    serverRegistrationNumberError,
    serverUsernameError,
    emailError,
    admissionDateError,
    dismissalDateError,
    usernameError,
    registrationNumberError,
    isFormValid,
    extractFieldErrorMessage,
  } = useUserFormValidation({
    ...params,
    isHydratingUserData,
  });

  const initialFormSnapshot = ref<EditUserSnapshot | null>(null);

  const buildFormSnapshot = (): EditUserSnapshot => ({
    name: params.name.value.trim(),
    registrationNumber: params.registrationNumber.value.trim(),
    username: params.username.value.trim(),
    email: params.email.value.trim(),
    admissionDate: isValidDate(params.admissionDate.value)
      ? params.admissionDate.value.getTime()
      : null,
    dismissalDate: isValidDate(params.dismissalDate.value)
      ? params.dismissalDate.value.getTime()
      : null,
    status: params.status.value,
    isSuperadmin: params.isSuperadmin.value,
    functionalActivities: params.functionalActivities.value.map((activity) =>
      String(activity.functionalActivityId ?? '')
    ),
  });

  const hasFormChanges = computed(() => {
    if (!initialFormSnapshot.value) return false;
    const currentSnapshot = buildFormSnapshot();
    return JSON.stringify(currentSnapshot) !== JSON.stringify(initialFormSnapshot.value);
  });

  const isSaveDisabled = computed(
    () =>
      !isFormValid.value ||
      !hasFormChanges.value ||
      params.isUpdating.value ||
      params.isLoadingUser.value
  );

  return {
    serverEmailError,
    serverRegistrationNumberError,
    serverUsernameError,
    registrationNumberError,
    emailError,
    admissionDateError,
    dismissalDateError,
    usernameError,
    isFormValid,
    hasFormChanges,
    isSaveDisabled,
    isHydratingUserData,
    initialFormSnapshot,
    buildFormSnapshot,
    extractFieldErrorMessage,
  };
}
